import {cast, Instance, types} from "mobx-state-tree";

import {getGqlModelBase} from "../gqlModel/GqlModelBase";

import {TaskList, TaskListUnit} from "./gql/serverTypes";
import {TaskListModel, TaskListModelType} from "./TaskListModel";
import type {TaskListUnitGqlProvider} from "./TaskListUnitGqlProvider";
import type {IDataClientService} from "../../platform/services/data/DataClientService";
import type {IAuthService} from "../../platform/services/auth/AuthService";


export type TaskListUnitModelType = Instance<typeof TaskListUnitModel>;

export interface TaskListUnitModelEnv {
    getDataClientService: () => IDataClientService;
    authService: IAuthService;
    queryProvider: TaskListUnitGqlProvider;
}

export const TaskListUnitModel = getGqlModelBase<TaskListUnit>()
    .named("TaskListUnitModel")
    .props({
        id: types.optional(types.identifier, ''),
        name: types.string,
        workspaceId: types.string,
        createdBy: types.optional(types.maybeNull(types.string), null),
        avatarPath: types.optional(types.maybeNull(types.string), null),
        description: types.optional(types.maybeNull(types.string), ''),
        timezone: types.optional(types.string, Intl.DateTimeFormat().resolvedOptions().timeZone),
        logicalDayShift: types.optional(types.integer, 4),
        editedBy: types.optional(types.maybeNull(types.string), null),
        taskLists: types.optional(types.map(TaskListModel), {}),
        taskListsOrder: types.optional(types.array(types.string), []),
        isArchived: types.optional(types.boolean, false),
        version: types.optional(types.number, 0),

        createdAt: types.optional(types.string, ''),
        updatedAt: types.optional(types.string, ''),
    })
    .views(self => ({
        get hasError() {
            if (!self.name) {
                return true;
            }
            return false;
        },
        get hasChangesRecursive() {
            return (
                self.hasChanges
                || Array.from(self.taskLists.values()).some(taskListModel => taskListModel.hasChanges)
            );
        },
        // Example of how to access task lists
        get orderedTaskLists(): TaskListModelType[] {
            if (!self.taskListsOrder) {
                return [];
            }
            return self.taskListsOrder
                .map(id => self.taskLists.get(id))
                .filter((taskList): taskList is TaskListModelType => taskList !== undefined);
        }
    }))
    .actions(self => {
        return {
            proceedSaveResults(obj: TaskListUnit) {
                // @ts-ignore
                self.lastSavedObject = obj!;
                self.version = obj.version;
            },
        };
    })
    .actions(self => ({
        // Action to add a new task list
        setTaskList(taskList: TaskList): TaskListModelType {
            self.taskLists.put(taskList);
            if (!self.taskListsOrder.includes(taskList.id)) {
                self.taskListsOrder.push(taskList.id);
            }
            self.hasChanges = true;
            return self.taskLists.get(taskList.id)!;
        },
        // Action to remove a task list
        removeTaskList(id: string) {
            self.taskLists.delete(id);
            self.taskListsOrder = cast(self.taskListsOrder.filter(taskId => taskId !== id));
            self.hasChanges = true;
        },
        setAvatarPath(path: string | null) {
            self.avatarPath = path;
        },
        setDescription(description: string) {
            self.description = description;
            self.hasChanges = true;
        },
        setName(name: string) {
            self.name = name;
            self.hasChanges = true;
        },
        reorder(startIndex: number, endIndex: number) {
            const [movedId] = self.taskListsOrder.splice(startIndex, 1);

            self.taskListsOrder.splice(endIndex, 0, movedId);
            self.hasChanges = true;
        },
    }))
